import React, { useState } from "react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Envelope, ChatCircleDots, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

const SUBJECTS = [
  "Question sur le service",
  "Problème technique / bug",
  "Suggestion / amélioration",
  "Demande de partenariat",
  "Signaler un contenu",
  "Demande RGPD (accès / suppression)",
  "Presse / média",
  "Autre",
];

export default function Contact() {
  const { user } = useAuth() || {};
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    subject: SUBJECTS[0],
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message envoyé !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <Layout>
        <SEO title="Contact" description="Contacter l'équipe Doudou Alert." />
        <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="contact-success">
          <CheckCircle size={72} weight="fill" className="mx-auto text-emerald-500" />
          <h1 className="font-display text-3xl font-black text-stone-800 mt-5">Message bien reçu !</h1>
          <p className="text-stone-600 mt-3 leading-relaxed">
            Merci pour votre message {form.name?.split(" ")[0]}. Nous répondons habituellement sous 48 heures à l'adresse <strong>{form.email}</strong>.
          </p>
          <Button onClick={() => { setDone(false); setForm({ ...form, message: "", subject: SUBJECTS[0] }); }} variant="outline" className="rounded-full mt-8">
            Envoyer un autre message
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO title="Contact" description="Contactez l'équipe Doudou Alert pour toute question, suggestion ou demande RGPD. Réponse sous 48 heures." />
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="contact-page">
        <div className="text-center mb-10">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">Nous écrire</span>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-stone-800 mt-2" data-testid="contact-title">
            On vous répond avec <span className="text-rose-500">attention</span>.
          </h1>
          <p className="text-stone-600 mt-3 max-w-xl mx-auto">
            Une question, un bug, une idée, une demande de partenariat ? Écrivez-nous, nous répondons à chaque message à la main, en général sous 48 heures.
          </p>
        </div>

        <div className="grid md:grid-cols-[1fr_2fr] gap-8 items-start">
          {/* Side info */}
          <aside className="bg-gradient-to-br from-rose-100 to-orange-100 rounded-3xl p-6 space-y-5">
            <div>
              <div className="w-12 h-12 rounded-2xl bg-white text-orange-600 grid place-items-center mb-3">
                <Envelope size={22} weight="duotone" />
              </div>
              <p className="font-display font-bold text-stone-800">Email direct</p>
              <a href="mailto:contact@doudou-alert.fr" className="text-orange-700 text-sm hover:underline" data-testid="contact-direct-email">
                contact@doudou-alert.fr
              </a>
            </div>
            <div>
              <div className="w-12 h-12 rounded-2xl bg-white text-rose-600 grid place-items-center mb-3">
                <ChatCircleDots size={22} weight="duotone" />
              </div>
              <p className="font-display font-bold text-stone-800">Délai de réponse</p>
              <p className="text-sm text-stone-600">48 heures en moyenne. Du lundi au vendredi.</p>
            </div>
            <p className="text-xs text-stone-500 leading-relaxed pt-2 border-t border-rose-200">
              💌 Pour les <strong>demandes RGPD</strong> (accès, rectification, suppression de vos données),
              utilisez le sujet correspondant. Nous traitons votre demande sous 30 jours, conformément à la loi.
            </p>
          </aside>

          {/* Form */}
          <form onSubmit={submit} className="bg-white rounded-3xl border border-rose-100 p-7 space-y-5 shadow-sm">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ct-name">Votre prénom et nom *</Label>
                <Input id="ct-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-2xl mt-1 py-6" placeholder="Sophie Dupont" data-testid="contact-name-input" />
              </div>
              <div>
                <Label htmlFor="ct-email">Email *</Label>
                <Input id="ct-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-2xl mt-1 py-6" placeholder="sophie@famille.fr" data-testid="contact-email-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="ct-subject">Sujet *</Label>
              <select id="ct-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full mt-1 rounded-2xl border-2 border-rose-100 px-4 py-3 bg-white" data-testid="contact-subject-select">
                {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="ct-message">Votre message *</Label>
              <Textarea id="ct-message" required minLength={10} rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-2xl mt-1" placeholder="Décrivez votre demande en quelques lignes..." data-testid="contact-message-input" />
              <p className="text-xs text-stone-400 mt-1">Minimum 10 caractères. {form.message.length}/5000</p>
            </div>
            <Button type="submit" disabled={sending} className="w-full bg-orange-500 hover:bg-orange-600 rounded-full py-6 font-bold shadow-lg shadow-orange-500/30" data-testid="contact-submit-button">
              <PaperPlaneTilt size={20} weight="bold" className="mr-2" />
              {sending ? "Envoi en cours..." : "Envoyer mon message"}
            </Button>
            <p className="text-xs text-stone-500 text-center">
              En envoyant ce formulaire, vous acceptez que vos coordonnées soient utilisées uniquement pour vous répondre.
            </p>
          </form>
        </div>
      </div>
    </Layout>
  );
}
